import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { LexicalEditor as LexicalEditorType } from 'lexical';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  Mutation,
  MutationConversationSendSmsArgs,
  ContactInteractionChannelResourceUnion,
  ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { DrawerSelectableOption } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { FlexColumn } from 'src/components/layout/flex-column';
import {
  ActiveAiAgentBanner,
  useHasActiveAiAgent,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/active-ai-agent-banner';
import {
  SmsInternationalNumberBanner,
  SmsLimitReachedBanner,
  SmsOptOutBanner,
  SmsResourceNotLinkedBanner,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/sms/sms';
import { SmsCounter } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/sms/sms-counter';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { CustomEditor } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/custom-editor';
import {
  CustomEditorProps,
  useHandleReplyError,
  useMapContactAddressesToOptions,
} from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/editors/helpers';
import { CONVERSATION_SEND_SMS } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/graphql-operations';
import { ResourceSelectorBar } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/resource-selector-bar';
import { ContactPhoneOption } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/shared';
import { useAuthorization } from 'src/hooks/use-authorization';
import { usePhoneNumberValidationUtils } from 'src/utils/phone-number';

const SmsCounterContainer = styled.div`
  margin-right: 10px;

  ${up('md')} {
    margin-right: 15px;
  }
`;

export const SmsEditor = memo<CustomEditorProps>(
  ({ handleFetchNewMessageCompleted, channelAvailability }) => {
    const {
      messageValues,
      attachmentList,
      contactPhoneNumbers,
      conversationId,
      hasFocus,
    } = useContext(ContactInteractionInputManagerContext);

    const [selectedResource, setSelectedResource] =
      useState<ContactInteractionChannelResourceUnion | null>(null);
    const [selectedContactAddress, setSelectedContactAddress] =
      useState<ContactPhoneOption | null>(null);
    const [selectedResourceAvailability, setSelectedResourceAvailability] =
      useState<ContactInteractionChannelAvailabilityForContactResourceToContactAddressAvailability | null>(
        null,
      );

    const { mapContactAddressesToOptions } = useMapContactAddressesToOptions();
    const { t } = useTranslation(['errors', 'conversations', 'common', 'smsChannel']);
    const handleReplyError = useHandleReplyError();
    const hasActiveAiAgent = useHasActiveAiAgent();
    const { getFeatureFlag } = useAuthorization();
    const isAiToolsFeatureEnabled = getFeatureFlag('AI');
    const isMessageTemplatesFeatureEnabled = getFeatureFlag(
      'contact-interaction-templates',
    );
    const { isPhoneNumberRegionSupportedForSms } = usePhoneNumberValidationUtils();
    const [sendSms, { loading: isSendingSms }] = useMutation<
      Pick<Mutation, 'conversationSendSms'>,
      MutationConversationSendSmsArgs
    >(CONVERSATION_SEND_SMS, {
      onError: handleReplyError,
    });

    useEffect(() => {
      if (!channelAvailability) {
        return;
      }
      const { resources, lastUsedResourceId, lastInteractionContactAddress } =
        channelAvailability;

      setSelectedResourceAvailability(
        channelAvailability.resourceToContactAddressAvailability?.find(
          (resource) =>
            resource.resourceId === selectedResource?.id &&
            resource.contactAddress === selectedContactAddress?.e164,
        ) ?? null,
      );

      if (!selectedResource) {
        const defaultSelectedResource =
          resources.find(({ id }) => id === lastUsedResourceId) ?? resources[0] ?? null;

        setSelectedResource(defaultSelectedResource);
      }

      if (!selectedContactAddress) {
        const defaultSelectedContactAddress: ContactPhoneOption | null =
          contactPhoneNumbers.find(
            (phone) => phone.e164 === lastInteractionContactAddress,
          ) ??
          contactPhoneNumbers[0] ??
          null;

        setSelectedContactAddress(defaultSelectedContactAddress);
      }
    }, [
      contactPhoneNumbers,
      channelAvailability,
      selectedResource,
      selectedContactAddress,
    ]);

    const handleSendMessage = useCallback(
      async (editor: LexicalEditorType) => {
        if (!conversationId || isSendingSms) {
          return;
        }

        if (
          selectedContactAddress &&
          !isPhoneNumberRegionSupportedForSms(selectedContactAddress)
        ) {
          return;
        }

        let commonVariables: Pick<
          MutationConversationSendSmsArgs,
          'conversationId' | 'message' | 'attachments'
        > = {
          conversationId,
          message: messageValues.message ?? '',
          attachments: [],
        };

        const attachments = attachmentList.map(({ url }) => ({
          s3UploadedUrl: url ?? '',
        }));

        if (attachments.length > 0) {
          commonVariables = {
            ...commonVariables,
            attachments,
          };
        }

        await sendSms({
          variables: {
            ...commonVariables,
            contactPhoneType: selectedContactAddress?.type ?? null,
            fromResourceId: selectedResource?.id ? Number(selectedResource.id) : null,
          },
          onCompleted: async ({ conversationSendSms: data }) => {
            await handleFetchNewMessageCompleted({
              editor,
              where: {
                contactInteractionId: {
                  eq: Number(data.interaction.id),
                },
              },
            });
          },
        });
      },
      [
        conversationId,
        isSendingSms,
        selectedContactAddress,
        isPhoneNumberRegionSupportedForSms,
        messageValues.message,
        attachmentList,
        sendSms,
        selectedResource?.id,
        handleFetchNewMessageCompleted,
      ],
    );

    const isSmsResourceNotTcrLinked = useMemo(
      () =>
        selectedResource?.__typename === 'ContactInteractionChannelSmsResource' &&
        selectedResource.unavailableReasonCode === 'NO_CAMPAIGN',
      [selectedResource],
    );

    const isSmsResourceLimitReached = useMemo(
      () =>
        selectedResource?.__typename === 'ContactInteractionChannelSmsResource' &&
        selectedResource.unavailableReasonCode === 'SMS_LIMIT_REACHED',
      [selectedResource],
    );

    const isSmsResourceOptOut = useMemo(
      () =>
        selectedResource?.__typename === 'ContactInteractionChannelSmsResource' &&
        selectedResourceAvailability?.unavailableReasonCode === 'OPT_OUT',
      [selectedResource, selectedResourceAvailability],
    );

    const isInternationalNumber = useMemo(
      () =>
        selectedContactAddress
          ? !isPhoneNumberRegionSupportedForSms(selectedContactAddress)
          : false,
      [isPhoneNumberRegionSupportedForSms, selectedContactAddress],
    );

    const isInputEnabled = useMemo(
      () =>
        !hasActiveAiAgent &&
        !isSmsResourceNotTcrLinked &&
        !isSmsResourceLimitReached &&
        !isSmsResourceOptOut &&
        !isInternationalNumber,
      [
        hasActiveAiAgent,
        isSmsResourceLimitReached,
        isSmsResourceNotTcrLinked,
        isSmsResourceOptOut,
        isInternationalNumber,
      ],
    );

    const mapSmsResourcesToGroupedOptions = ({
      resources,
      testIdGenerator,
    }: {
      resources: ContactInteractionChannelResourceUnion[];
      testIdGenerator: (id: string | number) => string;
    }) => {
      return resources.reduce<{
        linked: DrawerSelectableOption[];
        notLinked: DrawerSelectableOption[];
      }>(
        (acc, resource) => {
          const { displayName, address, id: resourceId } = resource;

          if (resource.__typename === 'ContactInteractionChannelSmsResource') {
            const option = {
              label: resource.phoneNumber?.national ?? address,
              onClick: () => setSelectedResource(resource),
              subTitle: displayName ?? undefined,
              value: resourceId,
              'data-lgg-id': testIdGenerator(resourceId),
            };

            if (resource.unavailableReasonCode === 'NO_CAMPAIGN') {
              acc.notLinked.push(option);
            } else {
              acc.linked.push(option);
            }
          }

          return acc;
        },
        { linked: [], notLinked: [] },
      );
    };

    const getSmsResourceSelectorBar = useCallback(
      ({ testIdPrefix }: { testIdPrefix: string }) => {
        const resources = channelAvailability?.resources ?? [];

        return selectedResource && resources.length > 0 ? (
          <ResourceSelectorBar
            label={t('common:from')}
            selectedResource={selectedResource}
            options={(() => {
              const testIdGenerator = (id) => `${testIdPrefix}-option-${id}`;

              const groupedOptions = mapSmsResourcesToGroupedOptions({
                resources,
                testIdGenerator,
              });

              return {
                [t('smsChannel:campaignRegistry.a2pVerified')]: groupedOptions.linked,
                [t('smsChannel:campaignRegistry.notVerified')]: groupedOptions.notLinked,
              };
            })()}
            data-lgg-id={`${testIdPrefix}-selector-dropdown-button`}
          />
        ) : null;
      },
      [channelAvailability?.resources, selectedResource, t],
    );

    const getContactPhoneSelectorBar = useCallback(() => {
      return selectedContactAddress && contactPhoneNumbers.length > 0 ? (
        <ResourceSelectorBar
          label={t('common:to')}
          selectedResource={selectedContactAddress}
          options={mapContactAddressesToOptions({
            phoneOptions: contactPhoneNumbers,
            onClick: setSelectedContactAddress,
          })}
          data-lgg-id="contact-phone-selector-dropdown-button"
        />
      ) : null;
    }, [contactPhoneNumbers, mapContactAddressesToOptions, selectedContactAddress, t]);

    return (
      <CustomEditor
        handleSubmission={handleSendMessage}
        isSubmitting={isSendingSms}
        bottomContent={{
          visible: true,
        }}
        topContent={
          hasFocus ||
          hasActiveAiAgent ||
          isSmsResourceNotTcrLinked ||
          isSmsResourceLimitReached ||
          isSmsResourceOptOut ||
          isInternationalNumber ? (
            <FlexColumn>
              {!hasActiveAiAgent ? (
                <>
                  {getSmsResourceSelectorBar({
                    testIdPrefix: 'sms-resource',
                  })}
                  {getContactPhoneSelectorBar()}
                </>
              ) : null}

              {hasActiveAiAgent ? (
                <ActiveAiAgentBanner />
              ) : isSmsResourceNotTcrLinked ? (
                <SmsResourceNotLinkedBanner />
              ) : isSmsResourceLimitReached ? (
                <SmsLimitReachedBanner />
              ) : isSmsResourceOptOut ? (
                <SmsOptOutBanner />
              ) : isInternationalNumber ? (
                <SmsInternationalNumberBanner />
              ) : null}
            </FlexColumn>
          ) : null
        }
        pluginConfig={{
          emojiPicker: {
            visible: true,
          },
          messageAttachments: {
            visible: true,
          },
          aiTools: {
            visible: isAiToolsFeatureEnabled,
          },
          messageTemplates: {
            visible: isMessageTemplatesFeatureEnabled,
          },
          inv360: {
            visible: true,
          },
        }}
        rightRailLeadingContent={
          <SmsCounterContainer>
            <SmsCounter
              message={messageValues.message}
              attachmentCount={attachmentList.length}
            />
          </SmsCounterContainer>
        }
        isInputEnabled={isInputEnabled}
      />
    );
  },
);
